'use client'

import { type FC, useCallback } from 'react'
import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  useReactFlow,
  type Edge,
  type EdgeProps,
} from '@xyflow/react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { EdgeType } from '@/lib/pbx/flow/types'
import { useTranslations } from "next-intl"


// Edge type labels for display
const EDGE_TYPE_LABELS = {
  default: '',
  answered: 'answered',
  no_answer: 'noAnswer',
  busy: 'busy',
  failed: 'failed',
  human_handover: 'humanHandover',
  call_ended: 'callEnded',
  working_hours: 'workingHours',
  after_hours: 'afterHours',
} as const satisfies Record<EdgeType, string>

// Edge type colors
const EDGE_TYPE_COLORS: Record<EdgeType, string> = {
  default: 'stroke-gray-400',
  answered: 'stroke-green-500',
  no_answer: 'stroke-yellow-500',
  busy: 'stroke-orange-500',
  failed: 'stroke-red-500',
  human_handover: 'stroke-blue-500',
  call_ended: 'stroke-purple-500',
  working_hours: 'stroke-green-500',
  after_hours: 'stroke-orange-500',
}

interface DeletableEdgeData extends Record<string, unknown> {
  edgeType?: EdgeType
}

export const DeletableEdge: FC<EdgeProps<Edge<DeletableEdgeData>>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  selected,
  data,
}) => {
  const tUi = useTranslations("Pbx.PbxFlowEditorEdgesDeletableEdge")

  const { setEdges } = useReactFlow()
  const edgeType = data?.edgeType || 'default'

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  const onEdgeDelete = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      setEdges((edges) => edges.filter((edge) => edge.id !== id))
    },
    [id, setEdges]
  )

  const labelKey = EDGE_TYPE_LABELS[edgeType]
  const label = labelKey ? tUi(labelKey) : ''
  const colorClass = EDGE_TYPE_COLORS[edgeType]

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={style}
        className={cn(
          'transition-all',
          colorClass,
          selected && 'stroke-[3px]'
        )}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan flex items-center gap-1"
        >
          {/* Label badge */}
          {label && (
            <span key={label} className="text-[10px] px-1.5 py-0.5 rounded bg-background border text-muted-foreground whitespace-nowrap">
              {label}
            </span>
          )}

          {/* Delete button - visible on select or hover */}
          <button
            type="button"
            onClick={onEdgeDelete}
            className={cn(
              'w-5 h-5 rounded-full bg-background border border-red-300 text-red-500',
              'flex items-center justify-center',
              'hover:bg-red-100 hover:border-red-500 hover:scale-110',
              'transition-all duration-150',
              selected ? 'opacity-100' : 'opacity-70 hover:opacity-100'
            )}
            title={tUi("deleteConnectionOrSelectAndPressDelete")}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  )
}
